import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FunnelHeader } from '@/components/health-check/FunnelChrome';
import { US_STATES, loadIntake, captureMeta, submitCustomQuote, formatPhone } from '@/lib/bhc';
import '@/styles/business-health-check.css';

const AREAS = [
  'Multiple entities', 'Multiple states', 'Prior-year / multi-year cleanup',
  'Amended returns', 'Payroll corrections', 'Sales-tax corrections',
  'IRS / state representation', 'Trust or estate matters', 'Other',
];

export default function CustomQuote() {
  const saved = loadIntake();
  const reasons = Array.isArray(saved.scope_reasons) ? saved.scope_reasons : [];
  const [form, setForm] = useState({
    first: saved.first || '', last: saved.last || '', email: saved.email || '',
    phone: saved.phone || '', phone_ext: saved.phone_ext || '', business: saved.business || '', state: saved.state || '',
    areas: [], timeline: '', contact_pref: 'Email', detail: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggleArea = (a) => setForm((f) => ({ ...f, areas: f.areas.includes(a) ? (f.areas || []).filter((x) => x !== a) : [...f.areas, a] }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first.trim() || !form.last.trim() || !/.+@.+\..+/.test(form.email.trim()) || (form.phone || '').replace(/\D/g, '').length < 7) {
      setError('Please add your name, a valid email, and phone so we can send your proposal.');
      return;
    }
    if (form.detail.trim().length < 10) {
      setError("Please tell us a bit about what you need (a sentence or two) — it's how we build an accurate proposal.");
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await submitCustomQuote({
        contact: {
          salutation: saved.salutation || '', first_name: form.first.trim(), last_name: form.last.trim(),
          email: form.email.trim(), phone: form.phone, phone_ext: form.phone_ext || '', business: form.business, state: form.state,
          role: saved.role || '',
        },
        areas: form.areas, detail: form.detail.trim(), timeline: form.timeline, contact_pref: form.contact_pref,
        variant: saved.variant || 'business', scope_reasons: reasons,
        intake_snapshot: saved.intake || null, engagement_id: saved.engagement_id || null,
        meta: captureMeta(),
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      setError('Something went wrong sending your request. Please try again — or call us at (704) 599-3355.');
      setSubmitting(false);
    }
  };
  if (done) {
    return (
      <div className="bhc-root">
        <Helmet><title>Request received · Custom Proposal | KGOB</title>
        <meta name="description" content="Your custom proposal request has been received by KGOB CPAs & Advisors." /><meta name="robots" content="noindex" /></Helmet>
        <FunnelHeader />
        <main className="funnel"><div className="wrap funnel__grid funnel__grid--narrow">
          <div className="success" data-testid="quote-success">
            <div className="success__badge"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg></div>
            <span className="eyebrow eyebrow--center">Request received</span>
            <h1 style={{ marginTop: '14px' }}>Thank you — we'll be in touch shortly.</h1>
            <p className="lede" style={{ marginTop: '18px', maxWidth: '54ch', marginInline: 'auto' }}>A member of our team will review your situation and reach out with a clear, fixed-price proposal built around your requirements. No obligation.</p>
            <div className="actionbar" style={{ justifyContent: 'center', marginTop: '30px' }}>
              <Link data-testid="custom-quote-link-1" className="btn btn--ghost btn--lg" to="/business-health-check">Back to the Health Check</Link>
            </div>
          </div>
        </div></main>
      </div>
    );
  }

  return (
    <div className="bhc-root">
      <Helmet>
        <title>Request a custom proposal · Health Check | KGOB</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <FunnelHeader back={{ label: 'Back to overview', to: '/business-health-check' }} />

      <main className="funnel">
        <div className="wrap funnel__grid funnel__grid--narrow">
          <div className="funnel__head">
            <span className="eyebrow">A better fit for you</span>
            <h1>Let's build a proposal around your requirements.</h1>
            <p>Your situation is a little bigger than the standard fixed-fee Health Check — which is good to know now. Tell us what you need and we'll send a clear, fixed-price proposal. No obligation, no hourly surprises.</p>
          </div>

          {reasons.length > 0 && (
            <div className="callout" style={{ marginBottom: '20px' }} data-testid="quote-reasons">
              <strong>Why we're recommending this:</strong> based on your answers, we noticed {reasons.join(', ')}. Those are exactly the things a tailored proposal handles well.
            </div>
          )}

          <form onSubmit={onSubmit} noValidate data-testid="custom-quote-form">
            <div className="panel">
              <p className="panel__title">Your details</p>
              <div className="fieldset" style={{ marginTop: '20px' }}>
                <div className="row2">
                  <div className="field"><label className="field__q" htmlFor="cq_first">First name <span style={{ color: '#B4472F' }}>*</span></label><input className="input" id="cq_first" name="first" value={form.first} onChange={set} placeholder="First" data-testid="cq-first" /></div>
                  <div className="field"><label className="field__q" htmlFor="cq_last">Last name <span style={{ color: '#B4472F' }}>*</span></label><input className="input" id="cq_last" name="last" value={form.last} onChange={set} placeholder="Last" data-testid="cq-last" /></div>
                </div>
                <div className="row2" style={{ marginTop: '16px' }}>
                  <div className="field"><label className="field__q" htmlFor="cq_email">Email <span style={{ color: '#B4472F' }}>*</span></label><input className="input" id="cq_email" name="email" type="email" value={form.email} onChange={set} placeholder="you@company.com" data-testid="cq-email" /></div>
                  <div className="field"><label className="field__q" htmlFor="cq_phone">Phone <span style={{ color: '#B4472F' }}>*</span> <span style={{ fontWeight: 400, opacity: 0.65 }}>· ext. optional</span></label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input className="input" id="cq_phone" name="phone" type="tel" value={form.phone} onChange={(e) => set({ target: { name: 'phone', value: formatPhone(e.target.value) } })} placeholder="(704) 555-0100" data-testid="cq-phone" style={{ flex: 1, minWidth: 0 }} />
                      <input className="input" id="cq_phone_ext" name="phone_ext" inputMode="numeric" placeholder="Ext." title="Extension (optional — business lines)" value={form.phone_ext} onChange={(e) => set({ target: { name: 'phone_ext', value: e.target.value.replace(/\D/g, '').slice(0, 6) } })} data-testid="cq-phone-ext" style={{ flex: '0 0 76px', width: '76px' }} />
                    </div>
                  </div>
                </div>
                <div className="row2" style={{ marginTop: '16px' }}>
                  <div className="field"><label className="field__q" htmlFor="cq_biz">Business / entity</label><input data-testid="custom-quote-input-1" className="input" id="cq_biz" name="business" value={form.business} onChange={set} placeholder="Company, LLC or trust name" /></div>
                  <div className="field"><label className="field__q" htmlFor="cq_state">Primary state</label>
                    <select data-testid="custom-quote-select-3" className="select" id="cq_state" name="state" value={form.state} onChange={set}><option value="">Select…</option>{(US_STATES || []).map((s) => <option key={s}>{s}</option>)}</select>
                  </div>
                </div>
              </div>
            </div>

            <div className="panel">
              <p className="panel__title">What do you need help with?</p>
              <p className="panel__hint">Select all that apply.</p>
              <div className="optgrid" style={{ marginTop: '16px' }}>
                {AREAS.map((a) => (
                  <label className="opt" key={a}>
                    <input type="checkbox" checked={form.areas.includes(a)} onChange={() => toggleArea(a)} data-testid={`cq-area-${a}`} />
                    <span>{a}</span>
                  </label>
                ))}
              </div>
              <div className="row2" style={{ marginTop: '24px' }}>
                <div className="field"><label className="field__q" htmlFor="cq_timeline">Timeline</label>
                  <select data-testid="custom-quote-select-2" className="select" id="cq_timeline" name="timeline" value={form.timeline} onChange={set}><option value="">Select…</option><option>As soon as possible</option><option>Within 30 days</option><option>This quarter</option><option>Just exploring</option></select>
                </div>
                <div className="field"><label className="field__q" htmlFor="cq_pref">Preferred contact</label>
                  <select data-testid="custom-quote-select-1" className="select" id="cq_pref" name="contact_pref" value={form.contact_pref} onChange={set}><option>Email</option><option>Phone</option><option>Either</option></select>
                </div>
              </div>
              <div className="field" style={{ marginTop: '16px' }}>
                <label className="field__q" htmlFor="cq_detail">What do you need? <span style={{ color: '#B4472F' }}>*</span></label>
                <textarea className="input" id="cq_detail" name="detail" value={form.detail} onChange={set} required placeholder="A few sentences about your entities, states, where the books stand, and what's prompting this." data-testid="cq-detail" />
              </div>
            </div>

            {error && <div className="callout callout--warn" style={{ marginTop: '20px' }} data-testid="quote-error">{error}</div>}

            <div className="actionbar">
              <button type="submit" className="btn btn--primary btn--lg" disabled={submitting} data-testid="quote-submit">{submitting ? 'Sending…' : 'Request my proposal'} <span className="arrow">→</span></button>
              <span className="actionbar__hint">No payment, no obligation.</span>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
